/**
 * EmbedWidgetPage is the builder for the embeddable freelancer widget
 * (served from /embed/freelancer/:id by the Express server). The builder is
 * the page: pick style + quotes (reviews/testimonials, max 2), optionally
 * bake AggregateRating JSON-LD into the snippet, and copy the embed code.
 * The live auto-resizing preview renders inside a mock browser frame.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { MdContentCopy, MdLoop, MdOutlineVerifiedUser, MdStar } from 'react-icons/md';
import { BiSearch } from 'react-icons/bi';
import { IoIosArrowDown } from 'react-icons/io';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';

import config from '../../config';
import { apiBaseUrl } from '../../util/api';
import { trackIntercomEvent } from '../../util/intercom';
import {
  IFRAME_TITLE,
  SECTION_KEYS,
  buildEmbedPath,
  buildEmbedSnippet,
  snippetDimensions,
} from '../../util/embedSnippet';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  NamedLink,
  Page,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import { fetchEmbedPageData } from './EmbedWidgetPage.duck';

import css from './EmbedWidgetPage.css';

const PROVIDER_ROLES = ['freelancer', 'light-entrepreneur'];
const MAX_PINNED_QUOTES = 2;
const OPTION_PREVIEW_LENGTH = 90;
const TOAST_DURATION = 2500;
const REVIEW_TYPE_OF_PROVIDER = 'ofProvider';

const truncateOption = str =>
  str && str.length > OPTION_PREVIEW_LENGTH
    ? `${str.slice(0, OPTION_PREVIEW_LENGTH).trimEnd()}…`
    : str || '';

const SECTION_LABEL_IDS = {
  user: 'EmbedWidgetPage.sectionUser',
  rating: 'EmbedWidgetPage.sectionRating',
  billed: 'EmbedWidgetPage.sectionBilled',
  testimonials: 'EmbedWidgetPage.sectionCount',
};

const THEME_OPTIONS = [
  { value: 'light', labelId: 'EmbedWidgetPage.themeLight' },
  { value: 'dark', labelId: 'EmbedWidgetPage.themeDark' },
];
const LANGUAGE_OPTIONS = [
  { value: 'fi', labelId: 'EmbedWidgetPage.languageFi' },
  { value: 'en', labelId: 'EmbedWidgetPage.languageEn' },
];

// Custom check control: the global input styles (marketplaceIndex.css
// applies --marketplaceInputStyles with appearance:none to every bare
// <input>) make native checkboxes render without a visible checked state,
// so selection controls must draw their own check.
const CheckToggle = ({ checked, disabled, onToggle, label, help }) => (
  <button
    type="button"
    className={classNames(css.checkToggle, {
      [css.checkToggleChecked]: checked,
      [css.checkToggleDisabled]: disabled,
    })}
    onClick={onToggle}
    disabled={disabled}
    aria-pressed={checked}
  >
    <span className={classNames(css.checkBox, { [css.checkBoxChecked]: checked })}>
      {checked ? '✓' : ''}
    </span>
    <span className={css.checkText}>
      <span className={css.checkLabel}>{label}</span>
      {help ? <span className={css.checkHelp}>{help}</span> : null}
    </span>
  </button>
);

// Switch control for the schema opt-in — same reasoning as CheckToggle, but
// the design calls for a toggle switch here instead of a checkbox card.
const SwitchToggle = ({ checked, disabled, onToggle, label, help }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    className={classNames(css.switchToggle, { [css.checkToggleDisabled]: disabled })}
    onClick={onToggle}
    disabled={disabled}
  >
    <span className={classNames(css.switchTrack, { [css.switchTrackOn]: checked })}>
      <span className={css.switchKnob} />
    </span>
    <span className={css.checkText}>
      <span className={css.switchLabel}>{label}</span>
      {help ? <span className={css.switchHelp}>{help}</span> : null}
    </span>
  </button>
);

// Comments and quoted strings are enough color for the snippet to read as
// code in the dark editor — no need for a real HTML tokenizer.
const highlightSnippet = snippet =>
  snippet.split(/(<!--[\s\S]*?-->|"[^"]*")/g).map((part, i) =>
    part.startsWith('<!--') ? (
      <span key={i} className={css.codeComment}>
        {part}
      </span>
    ) : part.startsWith('"') ? (
      <span key={i} className={css.codeString}>
        {part}
      </span>
    ) : (
      part
    )
  );

const OptionGroup = ({ labelId, options, value, onChange }) => (
  <div className={css.optionGroup}>
    <span className={css.optionLabel}>
      <FormattedMessage id={labelId} />
    </span>
    <div className={css.optionButtons}>
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          className={classNames(css.optionButton, {
            [css.optionButtonActive]: value === option.value,
          })}
          onClick={() => onChange(option.value)}
        >
          <FormattedMessage id={option.labelId} />
        </button>
      ))}
    </div>
  </div>
);

const BENEFITS = [
  {
    titleId: 'EmbedWidgetPage.benefit1Title',
    bodyId: 'EmbedWidgetPage.benefit1Body',
    icon: MdOutlineVerifiedUser,
  },
  {
    titleId: 'EmbedWidgetPage.benefit2Title',
    bodyId: 'EmbedWidgetPage.benefit2Body',
    icon: BiSearch,
  },
  {
    titleId: 'EmbedWidgetPage.benefit3Title',
    bodyId: 'EmbedWidgetPage.benefit3Body',
    icon: MdLoop,
  },
];

const FAQ_ITEMS = [
  { qId: 'EmbedWidgetPage.faq1Q', aId: 'EmbedWidgetPage.faq1A' },
  { qId: 'EmbedWidgetPage.faq2Q', aId: 'EmbedWidgetPage.faq2A' },
  { qId: 'EmbedWidgetPage.faq3Q', aId: 'EmbedWidgetPage.faq3A' },
  { qId: 'EmbedWidgetPage.faq4Q', aId: 'EmbedWidgetPage.faq4A' },
];

export const EmbedWidgetPageComponent = props => {
  const {
    currentUser,
    testimonials,
    offPlatformReviews,
    reviews,
    onFetchData,
    scrollingDisabled,
    intl,
  } = props;

  const userId = currentUser?.id?.uuid || null;
  const profile = currentUser?.attributes?.profile || {};
  const displayName = profile.displayName || '';
  const isEligible = PROVIDER_ROLES.includes(profile.publicData?.account_role);

  const [sections, setSections] = useState(SECTION_KEYS);
  const [theme, setTheme] = useState('light');
  const [lang, setLang] = useState(config.locale === 'en' ? 'en' : 'fi');
  const [selectedRefs, setSelectedRefs] = useState([]);
  const [schemaEnabled, setSchemaEnabled] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [openFaqIds, setOpenFaqIds] = useState([]);

  const previewFrameRef = useRef(null);

  useEffect(() => {
    if (userId && isEligible) {
      onFetchData(userId);
    }
  }, [userId, isEligible, onFetchData]);

  // Auto-resize the preview iframe from the widget's height messages — the
  // same contract as the listener shipped in the snippet.
  useEffect(() => {
    const widgetOrigin = apiBaseUrl();
    const onMessage = e => {
      if (e.origin !== widgetOrigin) return;
      const data = e.data || {};
      if (data.type !== 'freedomly-embed-height' || !(data.height > 0)) return;
      const frame = previewFrameRef.current;
      if (frame && frame.contentWindow === e.source) {
        frame.style.height = `${data.height}px`;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // One list across all three sources: testimonials first (newest first, as
  // fetched), then reviews by recency. Only quotable items (with text).
  const pickerItems = useMemo(() => {
    const testimonialItems = testimonials
      .filter(x => x.content)
      .map(x => ({
        ref: `t.${x._id}`,
        kind: 'testimonial',
        typeLabelId: 'EmbedWidgetPage.typeTestimonial',
        content: x.content,
        author: [x.authorName, x.authorPosition].filter(Boolean).join(', '),
        rating: null,
        createdAt: x.createdAt,
      }));
    const offReviewItems = offPlatformReviews
      .filter(x => x.content)
      .map(x => ({
        ref: `r.${x._id}`,
        kind: 'review',
        typeLabelId: 'EmbedWidgetPage.typeReview',
        content: x.content,
        author: x.displayName || x.authorName || '',
        rating: x.reviewRating || null,
        createdAt: x.createdAt,
      }));
    const onReviewItems = reviews
      .filter(x => x?.attributes?.type === REVIEW_TYPE_OF_PROVIDER && x?.attributes?.content)
      .map(x => ({
        ref: `p.${x.id.uuid}`,
        kind: 'review',
        typeLabelId: 'EmbedWidgetPage.typeReview',
        content: x.attributes.content,
        author: x.author?.attributes?.profile?.displayName || '',
        rating: x.attributes.rating || null,
        createdAt: x.attributes.createdAt,
      }));
    const reviewItems = [...offReviewItems, ...onReviewItems].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    return [...testimonialItems, ...reviewItems];
  }, [testimonials, offPlatformReviews, reviews]);

  // Latest testimonial preselected (matches the widget's server-side
  // fallback); first quotable item when there are no testimonials.
  useEffect(() => {
    if (selectedRefs.length === 0 && pickerItems.length > 0) {
      setSelectedRefs([pickerItems[0].ref]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerItems]);

  // The widget must always have something to show: the last remaining
  // content item (a section or a highlighted quote) can't be turned off.
  const toggleQuote = ref => {
    setSelectedRefs(prev => {
      if (prev.includes(ref)) {
        return sections.length + prev.length > 1 ? prev.filter(x => x !== ref) : prev;
      }
      return prev.length < MAX_PINNED_QUOTES ? [...prev, ref] : prev;
    });
  };

  const toggleFaq = qId =>
    setOpenFaqIds(prev => (prev.includes(qId) ? prev.filter(id => id !== qId) : [...prev, qId]));

  const toggleSection = key => {
    setSections(prev => {
      if (prev.includes(key)) {
        return prev.length + selectedRefs.length > 1 ? prev.filter(k => k !== key) : prev;
      }
      // Keep the canonical order regardless of toggle order.
      return SECTION_KEYS.filter(k => prev.includes(k) || k === key);
    });
  };

  // Same combination rule as the widget's server-side aggregate: on-platform
  // provider ratings + off-platform ratings, mean to one decimal.
  const aggregate = useMemo(() => {
    const onRatings = reviews
      .filter(x => x?.attributes?.type === REVIEW_TYPE_OF_PROVIDER)
      .map(x => x.attributes.rating);
    const offRatings = offPlatformReviews.map(x => x.reviewRating);
    const ratings = [...onRatings, ...offRatings].filter(v => typeof v === 'number' && isFinite(v));
    const count = ratings.length;
    return count > 0
      ? { ratingValue: (ratings.reduce((a, b) => a + b, 0) / count).toFixed(1), reviewCount: count }
      : null;
  }, [reviews, offPlatformReviews]);

  const { width, height } = snippetDimensions(sections, selectedRefs.length);
  const embedPath = buildEmbedPath(userId, { theme, lang, sections, quotes: selectedRefs });
  const iframeTitle = IFRAME_TITLE[lang] || IFRAME_TITLE.fi;
  const profileUrl = `${config.canonicalRootURL}/u/${userId}`;
  // First name only, matching the name the widget itself shows (the server
  // strips the last initial from the public displayName in server/embed/data.js).
  const firstName = profile.firstName || displayName;
  const schema = schemaEnabled && aggregate ? { name: firstName, profileUrl, ...aggregate } : null;
  const snippet = buildEmbedSnippet({
    rootUrl: config.canonicalRootURL,
    userId,
    theme,
    lang,
    sections,
    quotes: selectedRefs,
    schema,
  });

  const showToast = message => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), TOAST_DURATION);
  };

  // Both copy buttons (code editor icon + CTA) share this handler.
  const handleCopyClick = () => {
    if (typeof window === 'undefined' || !navigator.clipboard) {
      return;
    }
    trackIntercomEvent('clickedWidgetCopy');
    navigator.clipboard
      .writeText(snippet)
      .then(() => showToast(intl.formatMessage({ id: 'EmbedWidgetPage.copySuccess' })))
      .catch(err => {
        console.error('Failed to copy embed snippet:', err);
        showToast(intl.formatMessage({ id: 'EmbedWidgetPage.copyError' }));
      });
  };

  const title = intl.formatMessage({ id: 'EmbedWidgetPage.pageTitle' });

  const heroSection = (
    <header className={css.hero}>
      <h1 className={css.heroTitle}>
        <FormattedMessage
          id="EmbedWidgetPage.heroTitle"
          values={{
            accent: (
              <span className={css.heroTitleAccent}>
                <FormattedMessage id="EmbedWidgetPage.heroTitleAccent" />
              </span>
            ),
          }}
        />
      </h1>
      <p className={css.heroSubtitle}>
        <FormattedMessage id="EmbedWidgetPage.heroSubtitle" />
      </p>
    </header>
  );

  const quotePicker =
    pickerItems.length > 0 ? (
      <div>
        <span className={css.builderLabel}>
          <FormattedMessage id="EmbedWidgetPage.quotesLabel" />
        </span>
        <div className={css.quoteList}>
          {pickerItems.map(item => {
            const selected = selectedRefs.includes(item.ref);
            const locked =
              (!selected && selectedRefs.length >= MAX_PINNED_QUOTES) ||
              (selected && sections.length + selectedRefs.length === 1);
            return (
              <button
                key={item.ref}
                type="button"
                className={classNames(css.quoteOption, {
                  [css.quoteOptionSelected]: selected,
                  [css.quoteOptionDimmed]: locked && !selected,
                })}
                onClick={() => toggleQuote(item.ref)}
                disabled={locked}
                aria-pressed={selected}
              >
                <span
                  className={classNames(css.quoteBadge, {
                    [css.quoteBadgeSelected]: selected,
                  })}
                >
                  {selected ? selectedRefs.indexOf(item.ref) + 1 : ''}
                </span>
                <span className={css.quoteText}>
                  <span className={css.quoteContent}>{truncateOption(item.content)}</span>
                  <span className={css.quoteMeta}>
                    {item.author ? <span>{item.author}</span> : null}
                    {item.rating ? <span className={css.quoteStars}>★ {item.rating}</span> : null}
                    <span
                      className={classNames(
                        css.quoteType,
                        item.kind === 'review' ? css.quoteTypeReview : css.quoteTypeTestimonial
                      )}
                    >
                      <FormattedMessage id={item.typeLabelId} />
                    </span>
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        <span className={css.builderHelp}>
          <FormattedMessage
            id="EmbedWidgetPage.quotesHelp"
            values={{ count: selectedRefs.length }}
          />
        </span>
      </div>
    ) : null;

  // Mock Google result showing what the AggregateRating markup can unlock:
  // breadcrumb + link title mimic how Google displays the profile page and
  // stay visible always; the rating row (the user's real aggregate) appears
  // only while the toggle is on, previewing exactly what the markup adds.
  const searchDomain = config.canonicalRootURL.replace(/^https?:\/\/(www\.)?/, '');
  const searchSlug = displayName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const whoAmI = profile.publicData?.whoAmI;
  const previewStars = Array.from({ length: 5 }, (_, i) => <MdStar key={i} />);

  const searchPreview = (
    <div className={css.searchPreview}>
      <span className={css.searchBreadcrumb}>{`${searchDomain} › ${searchSlug}`}</span>
      <span className={css.searchTitle}>{whoAmI ? `${displayName} – ${whoAmI}` : displayName}</span>
      {schemaEnabled && aggregate ? (
        <span className={css.searchRatingRow}>
          <span className={css.searchStars} aria-hidden="true">
            <span className={css.searchStarsBase}>{previewStars}</span>
            <span
              className={css.searchStarsFill}
              style={{ width: `${(Number(aggregate.ratingValue) / 5) * 100}%` }}
            >
              {previewStars}
            </span>
          </span>
          <span className={css.searchRatingText}>
            <FormattedMessage
              id="EmbedWidgetPage.schemaPreviewRating"
              values={{
                rating: intl.formatNumber(Number(aggregate.ratingValue), {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                }),
                count: aggregate.reviewCount,
              }}
            />
          </span>
        </span>
      ) : null}
    </div>
  );

  const schemaSection = (
    <div className={css.schemaCard}>
      <SwitchToggle
        checked={schemaEnabled && !!aggregate}
        disabled={!aggregate}
        onToggle={() => setSchemaEnabled(v => !v)}
        label={intl.formatMessage({ id: 'EmbedWidgetPage.schemaLabel' })}
        help={intl.formatMessage({
          id: aggregate ? 'EmbedWidgetPage.schemaHelp' : 'EmbedWidgetPage.schemaDisabledHint',
        })}
      />
      {searchPreview}
    </div>
  );

  const builderSection = (
    <section className={css.section} id="builder">
      <div className={css.builder}>
        <div className={css.builderControls}>
          <div className={css.controlGroup}>
            <div className={css.panelHeader}>
              <span className={css.panelBadge}>1</span>
              <h2 className={css.panelTitle}>
                <FormattedMessage id="EmbedWidgetPage.step1Title" />
              </h2>
            </div>
            <OptionGroup
              labelId="EmbedWidgetPage.themeLabel"
              options={THEME_OPTIONS}
              value={theme}
              onChange={setTheme}
            />
            <OptionGroup
              labelId="EmbedWidgetPage.languageLabel"
              options={LANGUAGE_OPTIONS}
              value={lang}
              onChange={setLang}
            />
          </div>
          <div>
            <span className={css.builderLabel}>
              <FormattedMessage id="EmbedWidgetPage.sectionsLabel" />
            </span>
            <div className={css.sectionList}>
              {SECTION_KEYS.map(key => (
                <CheckToggle
                  key={key}
                  checked={sections.includes(key)}
                  disabled={sections.includes(key) && sections.length + selectedRefs.length === 1}
                  onToggle={() => toggleSection(key)}
                  label={intl.formatMessage({ id: SECTION_LABEL_IDS[key] })}
                />
              ))}
            </div>
          </div>
          {quotePicker}
          {schemaSection}
        </div>

        <div className={css.builderOutput}>
          <div>
            <span className={css.builderLabel}>
              <FormattedMessage id="EmbedWidgetPage.previewLabel" />
            </span>
            {/* Mock browser frame around the live widget: the preview reads
                as "your site with the widget embedded". */}
            <div className={css.previewArea}>
              <div className={css.browserChrome}>
                <span className={css.chromeDots}>
                  <span className={css.chromeDot} />
                  <span className={css.chromeDot} />
                  <span className={css.chromeDot} />
                </span>
                <span className={css.chromeUrl}>
                  <FormattedMessage id="EmbedWidgetPage.previewUrl" />
                </span>
              </div>
              <div className={css.sitePage}>
                <div className={css.skeletonGroup}>
                  <span className={css.skeletonBar} style={{ width: '58%' }} />
                  <span className={css.skeletonBar} style={{ width: '37%' }} />
                </div>
                <iframe
                  key={embedPath}
                  ref={previewFrameRef}
                  className={css.widgetIframe}
                  src={`${apiBaseUrl()}${embedPath}`}
                  width={width}
                  height={height}
                  title={iframeTitle}
                />
                <div className={css.skeletonGroup}>
                  <span className={css.skeletonBar} style={{ width: '64%' }} />
                  <span className={css.skeletonBar} style={{ width: '43%' }} />
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className={css.panelHeader}>
              <span className={css.panelBadge}>2</span>
              <h2 className={css.panelTitle}>
                <FormattedMessage id="EmbedWidgetPage.step2Title" />
              </h2>
            </div>
            <div className={css.codeEditor}>
              <div className={css.codeEditorHeader}>
                <span className={css.codeLang}>HTML</span>
                <button
                  type="button"
                  className={css.codeCopyIcon}
                  onClick={handleCopyClick}
                  title={intl.formatMessage({ id: 'EmbedWidgetPage.copyButton' })}
                  aria-label={intl.formatMessage({ id: 'EmbedWidgetPage.copyButton' })}
                >
                  <MdContentCopy />
                </button>
              </div>
              <pre className={css.codeBody}>{highlightSnippet(snippet)}</pre>
            </div>
          </div>
          <div className={css.outputActions}>
            <button type="button" className={css.copyButton} onClick={handleCopyClick}>
              <MdContentCopy />
              <FormattedMessage id="EmbedWidgetPage.copyButton" />
            </button>
            <p className={css.pasteNote}>
              <FormattedMessage id="EmbedWidgetPage.pasteNote" />
            </p>
          </div>
        </div>
      </div>
    </section>
  );

  const benefitsSection = (
    <section className={css.section}>
      <div className={css.benefitsGrid}>
        {BENEFITS.map(benefit => {
          const Icon = benefit.icon;
          return (
            <div key={benefit.titleId} className={css.benefit}>
              <span className={css.benefitIcon}>
                <Icon />
              </span>
              <div>
                <h3 className={css.benefitTitle}>
                  <FormattedMessage id={benefit.titleId} />
                </h3>
                <p className={css.benefitBody}>
                  <FormattedMessage id={benefit.bodyId} />
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );

  const faqSection = (
    <section className={css.section}>
      <h2 className={css.sectionTitle}>
        <FormattedMessage id="EmbedWidgetPage.faqTitle" />
      </h2>
      <div className={css.faqList}>
        {FAQ_ITEMS.map(item => {
          const isOpen = openFaqIds.includes(item.qId);
          return (
            <div key={item.qId} className={css.faqItem}>
              <h3 className={css.faqQuestion}>
                <button
                  type="button"
                  className={css.faqToggle}
                  onClick={() => toggleFaq(item.qId)}
                  aria-expanded={isOpen}
                >
                  <FormattedMessage id={item.qId} />
                  <IoIosArrowDown className={isOpen ? css.faqChevronOpen : undefined} />
                </button>
              </h3>
              {isOpen ? (
                <p className={css.faqAnswer}>
                  <FormattedMessage id={item.aId} />
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );

  const notEligibleSection = (
    <section className={css.section}>
      <p className={css.notEligible}>
        <FormattedMessage id="EmbedWidgetPage.notEligible" />
      </p>
      <NamedLink name="ProfileSettingsPage" className={css.notEligibleLink}>
        <FormattedMessage id="EmbedWidgetPage.notEligibleLink" />
      </NamedLink>
    </section>
  );

  return (
    <Page
      className={css.root}
      title={title}
      scrollingDisabled={scrollingDisabled}
      noIndexing={true}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="EmbedWidgetPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.content}>
            {currentUser && !isEligible ? (
              notEligibleSection
            ) : (
              <>
                {heroSection}
                {builderSection}
                {benefitsSection}
                {faqSection}
              </>
            )}
          </div>
          {toastMessage ? <div className={css.toast}>{toastMessage}</div> : null}
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

EmbedWidgetPageComponent.defaultProps = {
  currentUser: null,
  testimonials: [],
  offPlatformReviews: [],
  reviews: [],
};

EmbedWidgetPageComponent.propTypes = {
  currentUser: PropTypes.object,
  testimonials: PropTypes.array,
  offPlatformReviews: PropTypes.array,
  reviews: PropTypes.array,
  onFetchData: PropTypes.func.isRequired,
  scrollingDisabled: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { testimonials, offPlatformReviews, reviews } = state.EmbedWidgetPage;
  return {
    currentUser,
    testimonials,
    offPlatformReviews,
    reviews,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchData: userId => dispatch(fetchEmbedPageData(userId)),
});

const EmbedWidgetPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(EmbedWidgetPageComponent);

export default EmbedWidgetPage;
